import React from 'react';
import { Link } from 'react-router-dom';
import {
  colors,
  gradients,
  typography,
  borderRadius,
  getCardStyle,
  getButtonStyle,
  getGradientTextStyle,
} from '../theme';

const AboutPage: React.FC = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #fafafe 0%, #e8e8f8 100%)',
      padding: '40px 20px',
      position: 'relative',
    }}>
      {/* Subtle star pattern */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 50px 50px, rgba(74, 63, 138, 0.1), transparent),
          radial-gradient(1px 1px at 100px 100px, rgba(212, 175, 55, 0.08), transparent),
          radial-gradient(1.5px 1.5px at 200px 150px, rgba(74, 63, 138, 0.08), transparent)
        `,
        backgroundSize: '250px 250px',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '40px',
        position: 'relative',
        zIndex: 1,
      }}>
        <Link to="/" style={{
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <span style={{
            fontSize: '40px',
            marginRight: '15px',
            color: colors.accent.gold,
            textShadow: `0 0 15px rgba(212, 175, 55, 0.3)`,
          }}>
            ★
          </span>
          <span style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['3xl'],
            fontWeight: typography.fontWeight.bold,
            ...getGradientTextStyle('gold'),
          }}>
            StarDecision
          </span>
        </Link>
        <h1 style={{
          fontFamily: typography.fontFamily.heading,
          fontSize: typography.fontSize['4xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.background.midnight,
          margin: '0 0 20px 0',
          letterSpacing: typography.letterSpacing.wide,
        }}>
          About StarDecision
        </h1>
        <p style={{
          fontSize: typography.fontSize.xl,
          color: colors.text.muted,
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          Discover how ancient wisdom meets modern technology to guide your most important life decisions.
        </p>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* What We Do */}
        <div style={{
          ...getCardStyle('light'),
          marginBottom: '24px',
        }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.primary.celestial,
            marginBottom: '20px',
            letterSpacing: typography.letterSpacing.wide,
          }}>
            <span style={{ color: colors.accent.gold, marginRight: '10px' }}>★</span>
            What We Do
          </h2>
          <p style={{
            fontSize: typography.fontSize.base,
            lineHeight: '1.8',
            color: colors.text.muted,
            marginBottom: '20px',
          }}>
            StarDecision combines advanced astrological calculations with crystal guidance to help you identify the optimal timing for your most important life decisions. Whether you're considering a career change, planning a wedding, starting a business, or making any major life transition, we provide personalized timing guidance based on your unique birth chart and current planetary transits.
          </p>
          <p style={{
            fontSize: typography.fontSize.base,
            lineHeight: '1.8',
            color: colors.text.muted,
            margin: 0,
          }}>
            Our platform analyzes planetary movements, lunar phases, and cosmic cycles to reveal windows of opportunity when the universe is most aligned with your intentions and goals.
          </p>
        </div>

        {/* How It Works */}
        <div style={{
          ...getCardStyle('light'),
          marginBottom: '24px',
        }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.primary.celestial,
            marginBottom: '30px',
            letterSpacing: typography.letterSpacing.wide,
          }}>
            <span style={{ color: colors.accent.gold, marginRight: '10px' }}>◈</span>
            How It Works
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '30px',
          }}>
            {[
              {
                step: '1',
                title: 'Enter Your Birth Date',
                description: 'Provide your birth date, time, and location for precise astrological calculations.',
              },
              {
                step: '2',
                title: 'Choose Your Decision',
                description: 'Select from categories like relationships, career, health, or business decisions.',
              },
              {
                step: '3',
                title: 'Get Cosmic Timing',
                description: 'Receive detailed timing guidance and crystal recommendations for success.',
              },
            ].map((item, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: gradients.goldButton,
                  borderRadius: borderRadius.full,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: typography.fontSize['2xl'],
                  fontWeight: typography.fontWeight.bold,
                  color: colors.background.midnight,
                  fontFamily: typography.fontFamily.heading,
                }}>
                  {item.step}
                </div>
                <h3 style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.semibold,
                  color: colors.background.midnight,
                  marginBottom: '10px',
                  fontFamily: typography.fontFamily.heading,
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.text.muted,
                  lineHeight: '1.6',
                  margin: 0,
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div style={{
          ...getCardStyle('light'),
          marginBottom: '24px',
        }}>
          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            color: colors.primary.celestial,
            marginBottom: '30px',
            letterSpacing: typography.letterSpacing.wide,
          }}>
            <span style={{ color: colors.accent.gold, marginRight: '10px' }}>☆</span>
            Premium Features
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
          }}>
            <div>
              <h4 style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.background.midnight,
                marginBottom: '12px',
              }}>
                Free Tier
              </h4>
              <ul style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.muted,
                paddingLeft: '20px',
                margin: 0,
                lineHeight: '2',
              }}>
                <li>5 calculations per month</li>
                <li>Personal life decisions</li>
                <li>30-day timing windows</li>
                <li>Basic astrological guidance</li>
              </ul>
            </div>

            <div style={{
              background: 'rgba(212, 175, 55, 0.1)',
              padding: '20px',
              borderRadius: borderRadius.md,
              border: `1px solid ${colors.border.gold}`,
            }}>
              <h4 style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.accent.goldDark,
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <span style={{ color: colors.accent.gold }}>★</span>
                Premium ($3.99/month)
              </h4>
              <ul style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.muted,
                paddingLeft: '20px',
                margin: 0,
                lineHeight: '2',
              }}>
                <li>Unlimited calculations</li>
                <li>All decision categories</li>
                <li>Crystal guidance & recommendations</li>
                <li>Extended timeframes (up to 1 year)</li>
                <li>Detailed planetary explanations</li>
                <li>Export & save calculations</li>
              </ul>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{
          background: gradients.primaryButton,
          padding: '48px 40px',
          borderRadius: borderRadius.xl,
          textAlign: 'center',
          color: colors.text.starWhite,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative stars */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(2px 2px at 20% 30%, rgba(212, 175, 55, 0.4), transparent),
              radial-gradient(1px 1px at 80% 50%, rgba(255, 255, 255, 0.3), transparent),
              radial-gradient(1.5px 1.5px at 50% 70%, rgba(212, 175, 55, 0.3), transparent)
            `,
            backgroundSize: '200px 200px',
            pointerEvents: 'none',
          }} />

          <h2 style={{
            fontFamily: typography.fontFamily.heading,
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold,
            marginBottom: '16px',
            position: 'relative',
            letterSpacing: typography.letterSpacing.wide,
          }}>
            Ready to Discover Your Perfect Timing?
          </h2>
          <p style={{
            fontSize: typography.fontSize.base,
            marginBottom: '28px',
            opacity: 0.9,
            position: 'relative',
            maxWidth: '500px',
            margin: '0 auto 28px',
          }}>
            Join thousands who trust StarDecision for life's biggest choices. Start with our free tier and upgrade when you're ready for premium features.
          </p>
          <Link to="/register" style={{ textDecoration: 'none' }}>
            <button style={{
              ...getButtonStyle('gold'),
              fontSize: typography.fontSize.lg,
              padding: '16px 32px',
              position: 'relative',
            }}>
              ★ Get Started Free
            </button>
          </Link>
          <div style={{ marginTop: '24px', position: 'relative' }}>
            <Link to="/" style={{
              color: colors.text.starWhite,
              textDecoration: 'none',
              fontSize: typography.fontSize.sm,
              opacity: 0.8,
            }}>
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
